import { useMemo, useState } from 'react'
import {
  AIDE_STATUT, LIBELLE_PALIER, LIBELLE_PERIODE, LIBELLE_STATUT, MAX_VEHICULES_SUPP, PALIERS, PERIODES,
  RACCOURCIS_PROLONGATION, STATUTS,
  ajouterMois, aDesErreurs, basePourProlonger, enDateLocale, finDeJour, libelleOptions, palierApresSaisie,
  proposeDesSupplementaires, tarifApresSaisie, traduireErreurAbonnement, valider, type ErreursSaisie, type Saisie,
} from '../api/abonnement'
import { dateCourte, dateLongue, fcfa } from '../api/format'
import { palierEffectifOrg, prixDuPalier } from '../api/mrr'
import type { Organisation, PalierCode, PeriodeFacturation, Plan, Source } from '../api/types'
import { Icone } from './Icone'
import { Modale } from './Modale'

/**
 * « Modifier l'abonnement » — deux temps : saisie, puis récapitulatif à
 * confirmer explicitement. L'écriture passe par `platform_definir_abonnement`
 * (0019, étendue en 0027 : véhicules supplémentaires et période de facturation) :
 * raison obligatoire, journalisée, refusée pour l'organisation opératrice.
 */
export function ModaleAbonnement({
  org,
  plans,
  maintenant,
  source,
  surFermer,
  surEnregistre,
}: {
  org: Organisation
  plans: Plan[] | null
  maintenant: Date
  source: Source
  surFermer: () => void
  /** Appelé après un enregistrement réussi (recharge les données). */
  surEnregistre: () => Promise<void>
}) {
  const nom = org.brand_name || org.name
  const actuel = useMemo(
    () => ({
      tier: org.plan_tier, status: org.sub_status, finPeriode: org.period_end,
      extra: org.extra_vehicles, periode: org.billing_period,
    }),
    [org],
  )
  const [saisie, setSaisie] = useState<Saisie>({
    tier: org.plan_tier,
    status: org.sub_status ?? 'active',
    fin: org.period_end ? enDateLocale(new Date(org.period_end)) : '',
    raison: '',
    extra: org.extra_vehicles,
    periode: org.billing_period,
  })
  const [etape, setEtape] = useState<'saisie' | 'confirmation'>('saisie')
  const [erreurs, setErreurs] = useState<ErreursSaisie>({})
  const [enCours, setEnCours] = useState(false)
  const [echec, setEchec] = useState<string | null>(null)

  const base = basePourProlonger(org.period_end, maintenant)
  const modifie = <K extends keyof Saisie>(k: K, v: Saisie[K]) => {
    setSaisie((s) => {
      const suivante = { ...s, [k]: v }
      // Un palier qui ne vend pas de véhicules supplémentaires les remet à 0 (comme la base).
      if (k === 'tier' && !proposeDesSupplementaires(plans, v as PalierCode)) suivante.extra = 0
      return suivante
    })
    setErreurs({})
    setEchec(null)
  }
  const supplementairesPossibles = proposeDesSupplementaires(plans, saisie.tier)
  /* Prix lu dans le catalogue (`plans`), jamais écrit en dur. */
  const prixSupp = plans?.find((p) => p.tier === 'pro')?.extra_vehicle_price_fcfa ?? null

  const prixLibelle = (t: PalierCode): string => {
    if (!plans) return ''
    const p = prixDuPalier(plans, t)
    return p === null ? 'sur devis' : `${fcfa(p)} / mois`
  }

  function verifier() {
    const e = valider(saisie, actuel, maintenant, plans)
    setErreurs(e)
    if (!aDesErreurs(e)) {
      setEtape('confirmation')
      return
    }
    // Amène le focus (et l'écran) sur le premier champ en erreur : la raison peut être sous la ligne de flottaison.
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('.boite [aria-invalid="true"]')?.focus()
    })
  }

  async function confirmer() {
    if (enCours) return
    setEnCours(true)
    setEchec(null)
    try {
      await source.definirAbonnement({
        org: org.org_id,
        tier: saisie.tier,
        status: saisie.status,
        finPeriode: saisie.fin ? finDeJour(saisie.fin) : null,
        raison: saisie.raison.trim(),
        extraVehicules: supplementairesPossibles ? saisie.extra : 0,
        periode: saisie.periode,
      })
    } catch (e) {
      setEchec(traduireErreurAbonnement(e))
      setEnCours(false)
      return
    }
    // L'écriture est faite : un échec de rechargement ne doit pas passer pour un échec d'écriture
    // (surEnregistre gère seul ses erreurs et ne lève jamais).
    await surEnregistre()
    setEnCours(false)
    surFermer()
  }

  const apres = palierApresSaisie(saisie, org.is_operator, maintenant)
  const tarif = tarifApresSaisie(saisie, org, plans)
  const avantAffiche = actuel.status ? `${LIBELLE_PALIER[org.plan_tier]} · ${LIBELLE_STATUT[actuel.status]}` : 'Aucun abonnement'

  const pied = etape === 'saisie' ? (
    <>
      <button type="button" className="btn btn-doux" onClick={surFermer}>Annuler</button>
      <button type="button" className="btn btn-plein" onClick={verifier}>Vérifier</button>
    </>
  ) : (
    <>
      <button type="button" className="btn btn-doux" onClick={() => setEtape('saisie')} disabled={enCours}>Retour</button>
      <button type="button" className="btn btn-plein" onClick={confirmer} disabled={enCours} data-focus-initial>
        {enCours ? 'Enregistrement…' : "Confirmer l'enregistrement"}
      </button>
    </>
  )

  return (
    <Modale
      titre="Modifier l'abonnement"
      sousTitre={nom}
      surFermer={surFermer}
      pied={pied}
    >
      {etape === 'saisie' ? (
        <form onSubmit={(e) => { e.preventDefault(); verifier() }} noValidate>
          <fieldset className="champ">
            <legend>Palier</legend>
            <div className="choix">
              {PALIERS.map((t) => (
                <label key={t} className={`choix-item${saisie.tier === t ? ' on' : ''}`}>
                  <input type="radio" name="palier" checked={saisie.tier === t} onChange={() => modifie('tier', t)} />
                  <span className="choix-nom">{LIBELLE_PALIER[t]}</span>
                  <span className="choix-sous">{prixLibelle(t)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="champ">
            <label htmlFor="ab-extra">Véhicules supplémentaires</label>
            <input
              id="ab-extra" type="number" min={0} max={MAX_VEHICULES_SUPP} step={1} inputMode="numeric"
              value={saisie.extra} disabled={!supplementairesPossibles}
              onChange={(e) => modifie('extra', e.target.value === '' ? 0 : Math.trunc(Number(e.target.value)))}
              aria-invalid={Boolean(erreurs.extra)} aria-describedby="ab-extra-aide"
            />
            <span className="aide" id="ab-extra-aide">
              {supplementairesPossibles
                ? `Relèvent d’autant le plafond de véhicules de l’organisation${prixSupp === null ? '' : ` (${fcfa(prixSupp)} / mois pièce, Starter et Pro)`}.`
                : 'Non proposés au palier Business : le plafond se négocie au devis.'}
            </span>
            {erreurs.extra && <span className="erreur" role="alert">{erreurs.extra}</span>}
          </div>

          <div className="champ">
            <label htmlFor="ab-periode">Facturation</label>
            <select id="ab-periode" value={saisie.periode} onChange={(e) => modifie('periode', e.target.value as PeriodeFacturation)}>
              {PERIODES.map((p) => <option key={p} value={p}>{LIBELLE_PERIODE[p]}</option>)}
            </select>
            <span className="aide">
              {tarif && tarif.mensuel !== null && tarif.du !== null
                ? `Prix : ${fcfa(tarif.mensuel)} / mois${org.gps_devices > 0 ? ` (dont ${org.gps_devices} boîtier${org.gps_devices > 1 ? 's' : ''} GPS actif${org.gps_devices > 1 ? 's' : ''})` : ''}`
                  + (saisie.periode === 'annual' ? ` — ${fcfa(tarif.du)} à régler pour 12 mois, soit ${fcfa(tarif.mrr)} / mois de MRR.` : '.')
                : 'Prix : sur devis (ou tarifs des plans illisibles).'}
            </span>
          </div>

          <div className="champ">
            <label htmlFor="ab-statut">Statut</label>
            <select id="ab-statut" value={saisie.status} onChange={(e) => modifie('status', e.target.value as Saisie['status'])}>
              {STATUTS.map((s) => <option key={s} value={s}>{LIBELLE_STATUT[s]}</option>)}
            </select>
            <span className="aide">{AIDE_STATUT[saisie.status]}</span>
          </div>

          <div className="champ">
            <label htmlFor="ab-fin">Fin de période</label>
            <input
              id="ab-fin" type="date" value={saisie.fin}
              onChange={(e) => modifie('fin', e.target.value)}
              aria-invalid={Boolean(erreurs.fin)} aria-describedby="ab-fin-aide"
            />
            <div className="raccourcis" role="group" aria-label="Prolonger à partir de la base">
              {RACCOURCIS_PROLONGATION.map((n) => (
                <button
                  key={n} type="button" className="puce-bouton"
                  onClick={() => modifie('fin', enDateLocale(ajouterMois(base, n)))}
                >
                  +{n} mois
                </button>
              ))}
              {saisie.fin && (
                <button type="button" className="puce-bouton discrete" onClick={() => modifie('fin', '')}>Aucune date</button>
              )}
            </div>
            <span className="aide" id="ab-fin-aide">
              Les raccourcis partent de {org.period_end && base.getTime() > maintenant.getTime()
                ? `la fin actuelle (${dateLongue(org.period_end)})`
                : `aujourd'hui (${dateCourte(maintenant.toISOString())})`}.
              Un abonnement « Actif » n'expire pas à cette date : elle sert de repère de renouvellement.
            </span>
            {erreurs.fin && <span className="erreur" role="alert">{erreurs.fin}</span>}
          </div>

          <div className="champ">
            <label htmlFor="ab-raison">Raison du changement <b aria-hidden="true">*</b></label>
            <textarea
              id="ab-raison" rows={3} value={saisie.raison} required
              placeholder={`Ex. Paiement Orange Money reçu le ${dateCourte(maintenant.toISOString())}`}
              onChange={(e) => modifie('raison', e.target.value)}
              aria-invalid={Boolean(erreurs.raison)}
            />
            <span className="aide">Obligatoire, conservée dans le journal avec votre nom et la date.</span>
            {erreurs.raison && <span className="erreur" role="alert">{erreurs.raison}</span>}
          </div>

          {erreurs.global && <p className="encart encart-alerte" role="alert"><Icone nom="alert" /> {erreurs.global}</p>}
        </form>
      ) : (
        <div>
          <p className="encart"><Icone nom="alert" /> Vérifiez avant d'enregistrer : le changement est immédiat et journalisé.</p>
          <table className="recap">
            <thead><tr><th /><th>Avant</th><th>Après</th></tr></thead>
            <tbody>
              <tr><th scope="row">Abonnement</th><td>{avantAffiche}</td><td className="fort">{LIBELLE_PALIER[saisie.tier]} · {LIBELLE_STATUT[saisie.status]}</td></tr>
              <tr><th scope="row">Fin de période</th><td>{dateLongue(org.period_end)}</td><td className="fort">{saisie.fin ? dateLongue(finDeJour(saisie.fin)) : '—'}</td></tr>
              <tr>
                <th scope="row">Options</th>
                <td>{org.sub_status ? libelleOptions(org.extra_vehicles, org.billing_period) : '—'}</td>
                <td className="fort">{libelleOptions(supplementairesPossibles ? saisie.extra : 0, saisie.periode)}</td>
              </tr>
              <tr>
                <th scope="row">Prix</th>
                <td>{org.sub_status ? 'selon l’abonnement actuel' : '—'}</td>
                <td className="fort">
                  {tarif && tarif.mensuel !== null && tarif.du !== null
                    ? `${fcfa(tarif.mensuel)} / mois${saisie.periode === 'annual' ? ` · ${fcfa(tarif.du)} / an` : ''}`
                    : 'sur devis'}
                </td>
              </tr>
              <tr>
                <th scope="row">Palier appliqué</th>
                <td>{LIBELLE_PALIER[palierEffectifOrg(org, maintenant)]}</td>
                <td className="fort">{LIBELLE_PALIER[apres]}</td>
              </tr>
            </tbody>
          </table>
          {apres !== saisie.tier && (
            <p className="aide">Le palier appliqué diffère du palier choisi : {AIDE_STATUT[saisie.status].toLowerCase()}</p>
          )}
          <h3 className="fiche-titre">Raison</h3>
          <p className="journal-note">{saisie.raison.trim()}</p>
          {echec && <p className="encart encart-alerte" role="alert"><Icone nom="alert" /> {echec}</p>}
        </div>
      )}
    </Modale>
  )
}
